import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import _ from 'lodash';
import { useAppSelector } from '../../../../../redux/hooks';
import { useProgramContext } from '../../../../../hooks/useProgramContext';
import useUserPrograms from '../../../../../queries/useUserPrograms';
import { useUserVisibleGroupIds } from '../hooks/useUserVisibleGroupIds';
import { useUserProgramSelection } from '../hooks/useProgramSelection';
import { UserProgramInfo } from '../utils/programTypes';
import UserProgramCard from './UserProgramCard';
import UserProgramDrawer from './UserProgramDrawer';
import ProgramColumn from './ProgramColumn';
import MySkeleton from '../../../../../components/Skeleton';

interface Buckets {
  notStarted: UserProgramInfo[];
  inProgress: UserProgramInfo[];
  completed: UserProgramInfo[];
}

const splitIntoBuckets = (programs: UserProgramInfo[]): Buckets =>
  programs.reduce<Buckets>(
    (acc, program) => {
      const progress = typeof program.progress === 'number' ? program.progress : 0;
      const status = program.status?.toLowerCase();
      if (status?.includes('complete') || progress >= 100) acc.completed.push(program);
      else if (progress > 0 || status?.includes('progress')) acc.inProgress.push(program);
      else acc.notStarted.push(program);
      return acc;
    },
    { notStarted: [], inProgress: [], completed: [] }
  );

const UserProgramBoard: React.FC = () => {
  const allGroupData = useAppSelector((state) => state.allGroups.data);
  const groupRolesData = useAppSelector((state) => state.groupRoleList.data);
  const groupRoles = useMemo(() => groupRolesData ?? [], [groupRolesData]);
  const oid = useAppSelector((state) => state.orgDetails.data?.oid);
  const { orgId, location } = useProgramContext();

  const userVisibleGroupIds = useUserVisibleGroupIds({
    isAdminView: false,
    allGroups: allGroupData?.groups,
    groupRoles,
    oid
  });

  const roleLabelsById = useMemo(() => {
    const ids = userVisibleGroupIds || [];
    return ids.reduce<Record<string, string[]>>((acc, id) => {
      const rolesForId = groupRoles
        .filter((each) => each.id === id && each.role !== 'admin')
        // The role code ("Mentee", "Mentor"), not the program's displayRole: the card shows the org's word for
        // it, and useUserPrograms decides which progress to load from it.
        .map((each) => _.startCase(each.role || each.displayRole));
      acc[id] = rolesForId.length ? rolesForId : ['User'];
      return acc;
    }, {});
  }, [userVisibleGroupIds, groupRoles]);

  const { items, isLoading } = useUserPrograms({
    groupIds: userVisibleGroupIds || [],
    roleLabelsById,
    cachedGroups: allGroupData?.groups || [],
    orgId,
    location
  });

  const { selectedProgram, openDrawer, openProgram, closeProgram } = useUserProgramSelection();

  const buckets = useMemo(() => splitIntoBuckets(items), [items]);

  const showFullSkeleton = isLoading && items.length === 0;

  if (!showFullSkeleton && items.length === 0) {
    return (
      <Box sx={{ p: 3, height: '100%', overflow: 'hidden' }}>
        <Typography sx={{ color: '#FDFDFD' }}>No programs available.</Typography>
      </Box>
    );
  }

  const renderCard = (program: UserProgramInfo, dim?: boolean) => (
    <Box key={program.id} sx={{ opacity: dim && !program.loading ? 0.4 : 1, transition: 'opacity 200ms ease' }}>
      {program.loading ? (
        <MySkeleton variant="rounded" height={168} />
      ) : (
        <UserProgramCard program={{ ...program, onClick: () => openProgram(program) }} />
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 2.5, height: '100%', overflow: 'auto' }}>
        <ProgramColumn
          title="Not started"
          variant="user"
          loading={showFullSkeleton}
          isEmpty={!showFullSkeleton && buckets.notStarted.length === 0}
          emptyText="Nothing here yet."
        >
          {buckets.notStarted.map((p) => renderCard(p))}
        </ProgramColumn>

        <ProgramColumn
          title="In progress"
          variant="user"
          loading={showFullSkeleton}
          isEmpty={!showFullSkeleton && buckets.inProgress.length === 0}
          emptyText="Nothing here yet."
        >
          {buckets.inProgress.map((p) => renderCard(p))}
        </ProgramColumn>

        <ProgramColumn
          title="Completed"
          variant="user"
          loading={showFullSkeleton}
          isEmpty={!showFullSkeleton && buckets.completed.length === 0}
          emptyText="Nothing here yet."
        >
          {buckets.completed.map((p) => renderCard(p, true))}
        </ProgramColumn>
      </Box>
      <UserProgramDrawer open={openDrawer} onClose={closeProgram} program={selectedProgram} />
    </Box>
  );
};

export default UserProgramBoard;
